import { RudderAnalytics } from '@rudderstack/analytics-js'
import { TrackedEventNames, UserInfo, PropertiesDict, AnalyticsCallback } from './types'

// RudderStack configuration
const rudderWriteKey = process.env.NEXT_PUBLIC_RUDDERSTACK_WRITE_KEY as string
const rudderDataPlaneUrl = process.env.NEXT_PUBLIC_RUDDERSTACK_DATA_PLANE_URL as string
const debug = (process.env.NEXT_PUBLIC_ANALYTICS_DEBUG_ENABLED as string) || false

// Initialize RudderStack
let rudderAnalytics: RudderAnalytics

if (typeof window !== 'undefined') {
  rudderAnalytics = new RudderAnalytics()
  rudderAnalytics.load(rudderWriteKey, rudderDataPlaneUrl, {
    logLevel: debug === 'true' ? 'DEBUG' : 'ERROR',
    integrations: { All: true }
  })
}

// Core tracking function that sends events to RudderStack
const trackEvent = (eventName: TrackedEventNames, props?: PropertiesDict, callBack?: AnalyticsCallback): void => {
  if (typeof window === 'undefined' || !rudderAnalytics) return

  const options = {
    ...props
  }

  try {
    rudderAnalytics.track(eventName, options, callBack as any)
  } catch (e) {
    console.error('Error tracking event', eventName, e)
  }
}

export const identifyUser = (user: UserInfo): void => {
  if (typeof window === 'undefined' || !rudderAnalytics) return
  if (!user.profileId) return

  const traits: Record<string, any> = {
    profileId: user.profileId,
    isStaff: !!user?.isStaff
  }

  // Add optional traits if they exist
  if (user.email) traits.email = user.email
  if (user.name) traits.name = user.name
  if (user.username) traits.username = user.username
  if (user.publicAddress) traits.publicAddress = user.publicAddress

  rudderAnalytics.identify(user.profileId.toString(), traits)
}

export const trackGenericButtonClickEvent = <T, P>(buttonName: T, props?: P, callBack?: AnalyticsCallback): void => {
  trackEvent(TrackedEventNames.buttonClicked, { 'Button name': buttonName, ...props }, callBack)
}

export const trackGenericTabChangedEvent = <T, P>(tabGroupName: T, props?: P, callBack?: AnalyticsCallback): void => {
  trackEvent(TrackedEventNames.tabChanged, { 'Tab group name': tabGroupName, ...props }, callBack)
}

export const trackGenericSwitchToggledEvent = <T, P>(switchName: T, props?: P, callBack?: AnalyticsCallback): void => {
  trackEvent(TrackedEventNames.switchToggled, { 'Switch name': switchName, ...props }, callBack)
}

export const trackPurchaseFlowEvent = <T, P>(purchaseFlow: T, props?: P, callBack?: AnalyticsCallback): void => {
  trackEvent(TrackedEventNames.purchaseFlow, { 'Purchase flow': purchaseFlow, ...props }, callBack)
}

export const trackGenericSelectionMadeEvent = <T, P>(
  selectorName: T,
  selectorValue: string | null,
  props?: P,
  callBack?: AnalyticsCallback
): void => {
  trackEvent(TrackedEventNames.selectionMade, { Selector: selectorName, Value: selectorValue, ...props }, callBack)
}

export const trackGenericGridItemClickedEvent = <T, L, P>(
  gridType: T,
  clickLocation: L,
  props?: P,
  callBack?: AnalyticsCallback
): void => {
  trackEvent(
    TrackedEventNames.gridItemClicked,
    { 'Grid type': gridType, 'Click location': clickLocation, ...props },
    callBack
  )
}

export const trackSearchEventFlow = <T>(
  step: T,
  searchTerm?: string,
  searchResult?: string,
  callBack?: AnalyticsCallback
): void => {
  trackEvent(
    TrackedEventNames.fullSiteSearch,
    {
      Step: step,
      'Search term': searchTerm,
      'Search result': searchResult
    },
    callBack
  )
}

export const trackPageEvent = (pageName: string): void => {
  if (typeof window === 'undefined' || !rudderAnalytics) return
  rudderAnalytics.page(pageName)
}

export const trackFarcasterEvent = <T, P>(frameName: T, props?: P, callBack?: AnalyticsCallback): void => {
  trackEvent(TrackedEventNames.farcasterFrameInteraction, { Frame: frameName, ...props }, callBack)
}

export interface ProductClickProperties {
  product_id: string
  category?: string
  name: string
  url?: string
}

export const trackProductClickedEvent = (
  product: ProductClickProperties,
  additionalProps?: PropertiesDict,
  callback?: AnalyticsCallback
): void => {
  trackEvent(
    TrackedEventNames.productClicked,
    {
      ...product,
      ...additionalProps
    },
    callback
  )
}

// Add TypeScript declaration for window object
declare global {
  interface Window {
    __pageViewStartTime?: number
  }
}
